/**
 * Cookie utility functions for managing authentication tokens
 */

type CookieOptions = {
  secure: boolean
  sameSite: 'lax' | 'strict'
  expires?: Date
}

/**
 * Checks if the current hostname is a local development environment
 * Includes localhost, 127.0.0.1, and private network IPs (192.168.x.x, 10.x.x.x, 172.x.x.x)
 */
export const isLocalDevelopment = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }

  const hostname = window.location.hostname

  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.') ||
    hostname.startsWith('172.')
  )
}

/**
 * Gets the appropriate cookie options based on the current environment
 * - Development (local/private IPs): secure: false, sameSite: 'lax' (works with HTTP)
 * - Production: secure: true, sameSite: 'strict' (requires HTTPS)
 *
 * @param rememberMe - If true, sets cookie to expire in 30 days. If false, the cookie expires when the browser closes (session cookie)
 */
export const getCookieOptions = (rememberMe: boolean = false): CookieOptions => {
  const baseOptions = isLocalDevelopment()
    ? { secure: false, sameSite: 'lax' as const }
    : { secure: true, sameSite: 'lax' as const }

  // If rememberMe is true, set cookie to expire in 30 days
  // If false, don't set expires (makes it a session cookie that expires when the browser closes)
  if (rememberMe) {
    const expiryDate = new Date()
    expiryDate.setDate(expiryDate.getDate() + 30) // 30 days
    return { ...baseOptions, expires: expiryDate }
  }

  return baseOptions
}
